<template>
  <div :class="classes">
    <slot />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-box>
   *   I'm in a box!
   * </ak-box>
   */
  name: 'AkBox',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description This determines whether the 'box--narrow' class
     * should be applied to the element.
     */
    narrow: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description This determines padding size class applied to the element.
     */
    size: {
      type: String,
      validator: (v) =>
        ['flush', 'tiny', 'small', 'large', 'huge'].indexOf(v) >= 0,
      default: null,
    },

    /**
     * @type {Boolean}
     * @description This determines palm mq padding size class applied to the element.
     */
    palmSize: {
      type: String,
      validator: (v) => ['flush', 'tiny', 'small', 'normal'].indexOf(v) >= 0,
      default: null,
    },

    /**
     * @type {Boolean}
     * @description This determines portable mq padding size class applied to the element.
     */
    portableSize: {
      type: String,
      validator: (v) => ['flush', 'tiny', 'small', 'normal'].indexOf(v) >= 0,
      default: null,
    },

    /**
     * @type {Boolean}
     * @description This determines lap mq padding size class applied to the element.
     */
    lapSize: {
      type: String,
      validator: (v) => ['flush', 'tiny', 'small', 'normal'].indexOf(v) >= 0,
      default: null,
    },

    /**
     * @type {Boolean}
     * @description This determines whether the 'b' class
     * should be applied to the element.
     */
    bordered: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean | String}
     * @description Rounded corners for all, top or bottom
     */
    rounded: {
      type: [Boolean, String],
      validator: (v) => ['top', 'bottom', true, false].indexOf(v) >= 0,
      default: false,
    },

    /**
     * @type {String}
     * @description Background color for the box.
     */
    background: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description This determines whether the box requires
     * the keyboard shortcut styling
     */
     shortcut: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classes = computed(() => {
      const res = {
        'ak-box': true,
        b: props.bordered === true,
        'ak-box--narrow': props.narrow === true,
        [`ak-box--${props.size}`]: props.size,
        [`ak-box--${props.palmSize}@palm`]: props.palmSize,
        [`ak-box--${props.portableSize}@portable`]: props.portableSize,
        [`ak-box--${props.lapSize}@lap`]: props.lapSize,
        'ak-box--rounded': props.rounded === true,
        [`ak-box--rounded-${props.rounded}`]:
          props.rounded === 'top' || props.rounded === 'bottom',
        'ak-box--keyboard-shortcut': props.shortcut === true,
      }

      if (props.background && props.background.length) {
        res[`bg-${props.background}`] = true
      }

      return res
    })

    return {
      classes,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
$box-padding: $spacing;
$box-padding--tiny: quarter($box-padding);
$box-padding--small: halve($box-padding);
$box-padding--large: double($box-padding);
$box-padding--huge: quadruple($box-padding);

.ak-box {
  @include clearfix();
  display: block;
  padding: $box-padding;
  @include no-space-bottom();

  &--flush {
    padding: 0;
  }

  &--tiny {
    padding: $box-padding--tiny;
  }

  &--small {
    padding: $box-padding--small;
  }

  &--large {
    padding: $box-padding--large;
  }

  &--huge {
    padding: $box-padding--huge;
  }

  &--narrow {
    padding: $box-padding--small $box-padding;

    &.ak-box--small {
      padding: $box-padding--tiny $box-padding--small;
    }

    &.ak-box--tiny {
      padding: halve($box-padding--tiny) $box-padding--small;
    }
  }

  &--rounded {
    border-radius: $base-border-radius;
    &-bottom {
      border-radius: 0 0 $base-border-radius $base-border-radius;
    }

    &-top {
      border-radius: $base-border-radius $base-border-radius 0 0;
    }
  }

  &--keyboard-shortcut {
    padding: 0;
    display: inline-block;
    vertical-align: middle;
    font-weight: bold;
    border-radius: $base-border-radius;
    border: 1px solid $color-grey-lighter;
    color: $color-grey-light;
    width: 1.75em;
    height: 1.75em;
    line-height: 1.5;
    text-align: center;
    text-shadow: none;
    font-size: 85%;

    &-dark {
      background-color: rgba(0,0,0,.5);
      color: #fff;
      border-color: #fff;
    }
  }
}

@media screen and #{$mq-palm} {
  .ak-box--normal\@palm {
    padding: $box-padding;
  }
  .ak-box--small\@palm {
    padding: $box-padding--small;
  }
  .ak-box--tiny\@palm {
    padding: $box-padding--tiny;
  }
  .ak-box--flush\@palm {
    padding: 0;
  }
}

@media screen and #{$mq-portable} {
  .ak-box--normal\@portable {
    padding: $box-padding;
  }
  .ak-box--small\@portable {
    padding: $box-padding--small;
  }
  .ak-box--tiny\@portable {
    padding: $box-padding--tiny;
  }
  .ak-box--flush\@portable {
    padding: 0;
  }
}

@media screen and #{$mq-lap} {
  .ak-box--normal\@lap {
    padding: $box-padding;
  }
  .ak-box--small\@lap {
    padding: $box-padding--small;
  }
  .ak-box--tiny\@lap {
    padding: $box-padding--tiny;
  }
  .ak-box--flush\@lap {
    padding: 0;
  }
}
</style>
